import axios from 'axios';

import { toBool } from '../../utils/formatters';
import { exportRunToCsv, mergeActiveRunsForward, normalizeActiveRuns } from '../../utils/testRigHelpers';

export const createRunControls = (deps) => {
  const {
    selectedDataset,
    datasets,
    modelName,
    resizeMode,
    compressionMode,
    useEbayRag,
    useNotesRag,
    assumeHiddenItems,
    isStartingRun,
    isRunInProgress,
    isRunning,
    isTerminatingAllRuns,
    isFlushing,
    loadingRunDetailsById,
    testResults,
    setIsStartingRun,
    setResizeMode,
    setCompressionMode,
    setUseEbayRag,
    setUseNotesRag,
    setAssumeHiddenItems,
    setSelectedDataset,
    setDatasetSearch,
    setIsRunning,
    setActiveRuns,
    setIsTerminatingAllRuns,
    setIsFlushing,
    setLoadingRunDetailsById,
    setTestResults,
    setExpandedRun,
    fetchData,
    fetchPromptTemplates,
    fetchDatasets,
  } = deps;

  const mergeUpdatedRunInState = (updatedRun) => {
    const normalizedRunId = String(updatedRun?.run_id || '').trim();
    if (!normalizedRunId) return;
    setTestResults((previousRuns) =>
      previousRuns.map((run) => {
        if (String(run?.run_id || '').trim() !== normalizedRunId) return run;
        return {
          ...run,
          ...updatedRun,
          results: Array.isArray(updatedRun?.results) ? updatedRun.results : run.results,
        };
      }),
    );
  };

  const fetchRunDetails = async (runId, { silent = false } = {}) => {
    const normalizedRunId = String(runId || '').trim();
    if (!normalizedRunId) return null;

    const existingRun = testResults.find(
      (run) => String(run?.run_id || '').trim() === normalizedRunId,
    );
    if (existingRun && Array.isArray(existingRun?.results)) {
      return existingRun;
    }
    if (loadingRunDetailsById[normalizedRunId]) {
      return null;
    }

    setLoadingRunDetailsById((previous) => ({ ...previous, [normalizedRunId]: true }));
    try {
      const response = await axios.get('/get_test_results', {
        params: { run_id: normalizedRunId },
      });
      const fetchedRuns = Array.isArray(response?.data) ? response.data : [];
      const fetchedRun =
        fetchedRuns.find((run) => String(run?.run_id || '').trim() === normalizedRunId) || null;
      if (!fetchedRun) return null;
      mergeUpdatedRunInState(fetchedRun);
      return fetchedRun;
    } catch (error) {
      console.error(`Error loading details for run ${normalizedRunId}:`, error);
      if (!silent) {
        const message =
          error?.response?.data?.message || error.message || 'Failed to load run details.';
        alert(message);
      }
      return null;
    } finally {
      setLoadingRunDetailsById((previous) => {
        const next = { ...previous };
        delete next[normalizedRunId];
        return next;
      });
    }
  };

  const runTestRig = async () => {
    if (isStartingRun) return;
    if (!selectedDataset) {
      alert('Please select a dataset before running the test rig.');
      return;
    }

    setIsStartingRun(true);
    try {
      const response = await axios.post('/run_test_rig', {
        model: modelName,
        resize_mode: resizeMode,
        compression_mode: compressionMode,
        use_ebay_rag: useEbayRag,
        use_notes_rag: useNotesRag,
        assume_hidden_items: assumeHiddenItems,
        dataset_prefix: selectedDataset.prefix || '',
      });
      if (response.data.status === 'success') {
        if (response.data.resize_mode) setResizeMode(response.data.resize_mode);
        if (response.data.compression_mode) setCompressionMode(response.data.compression_mode);
        if (response.data.use_ebay_rag !== undefined) {
          setUseEbayRag(toBool(response.data.use_ebay_rag, useEbayRag));
        }
        if (response.data.use_notes_rag !== undefined) {
          setUseNotesRag(toBool(response.data.use_notes_rag, useNotesRag));
        }
        if (response.data.assume_hidden_items !== undefined) {
          setAssumeHiddenItems(toBool(response.data.assume_hidden_items, assumeHiddenItems));
        }
        if (response.data.dataset_prefix !== undefined) {
          const selectedPrefix = response.data.dataset_prefix || '';
          const matchingDataset = datasets.find(
            (entry) => (entry.prefix || '') === selectedPrefix,
          );
          if (matchingDataset) {
            setSelectedDataset(matchingDataset);
            setDatasetSearch(matchingDataset.name || matchingDataset.prefix || '');
          }
        }
        setIsRunning(true);
        const responseActiveRuns = Array.isArray(response?.data?.active_runs)
          ? response.data.active_runs
          : [];
        const responseRunContext =
          response?.data?.run_context && typeof response.data.run_context === 'object'
            ? response.data.run_context
            : null;
        const optimisticRunContext =
          responseRunContext ||
          (response?.data?.run_id
            ? {
                run_id: response.data.run_id,
                started_at: response.data.started_at || new Date().toISOString(),
                model: response.data.model || modelName,
                resize_mode: response.data.resize_mode || resizeMode,
                compression_mode: response.data.compression_mode || compressionMode,
                use_ebay_rag: toBool(response.data.use_ebay_rag, useEbayRag),
                use_notes_rag: toBool(response.data.use_notes_rag, useNotesRag),
                assume_hidden_items: toBool(response.data.assume_hidden_items, assumeHiddenItems),
                dataset_prefix: response.data.dataset_prefix || (selectedDataset?.prefix || ''),
                dataset_name:
                  response.data.dataset_name ||
                  selectedDataset?.name ||
                  selectedDataset?.prefix ||
                  'N/A',
                total_data_points:
                  response.data.dataset_data_points ?? selectedDataset?.data_points ?? null,
                data_points:
                  response.data.dataset_data_points ?? selectedDataset?.data_points ?? null,
                attempted_data_points: 0,
                successful_data_points: 0,
                skipped_data_points: 0,
                progress_percent: 0,
                phase: 'starting',
              }
            : null);
        const optimisticRuns = normalizeActiveRuns(
          responseActiveRuns.length > 0
            ? responseActiveRuns
            : optimisticRunContext
              ? [optimisticRunContext]
              : [],
        );
        if (optimisticRuns.length > 0) {
          setActiveRuns((previous) => mergeActiveRunsForward(previous, optimisticRuns));
        }
        fetchPromptTemplates();
        fetchData({ showLoading: false });
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      if (error.response && error.response.status === 400) {
        const message = error.response?.data?.message || 'Unable to start test rig.';
        alert(message);
        if (message.toLowerCase().includes('was not found')) {
          setSelectedDataset(null);
          fetchDatasets();
        }
      } else {
        alert(`Error starting test rig: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setIsStartingRun(false);
    }
  };

  const terminateRun = async (runId) => {
    const normalizedRunId = String(runId || '').trim();
    if (!normalizedRunId) {
      alert('Cannot terminate this run because run_id is missing.');
      return;
    }
    if (!window.confirm(`Terminate experiment ${normalizedRunId.slice(0, 8)}?`)) return;

    deps.setTerminatingRunIds((previous) => ({ ...previous, [normalizedRunId]: true }));
    try {
      const response = await axios.post('/terminate_test_rig', { run_id: normalizedRunId });
      if (response?.data?.status === 'success' || response?.data?.status === 'partial_success') {
        fetchData({ showLoading: false });
      } else {
        alert(response?.data?.message || 'Failed to terminate run.');
      }
    } catch (error) {
      const message = error?.response?.data?.message || error.message || 'Failed to terminate run.';
      alert(message);
    } finally {
      deps.setTerminatingRunIds((previous) => {
        const next = { ...previous };
        delete next[normalizedRunId];
        return next;
      });
    }
  };

  const terminateAllRuns = async () => {
    if (!window.confirm('Terminate all currently running experiments?')) return;
    if (isTerminatingAllRuns) return;
    setIsTerminatingAllRuns(true);
    try {
      const response = await axios.post('/terminate_test_rig', { terminate_all: true });
      if (response?.data?.status === 'success' || response?.data?.status === 'partial_success') {
        fetchData({ showLoading: false });
      } else {
        alert(response?.data?.message || 'Failed to terminate active experiments.');
      }
    } catch (error) {
      const message =
        error?.response?.data?.message || error.message || 'Failed to terminate active experiments.';
      alert(message);
    } finally {
      setIsTerminatingAllRuns(false);
    }
  };

  const flushResults = async () => {
    if (!window.confirm('Are you sure you want to clear all test results? This cannot be undone.')) return;
    if (isRunInProgress) {
      alert('Cannot flush results while test rig is running.');
      return;
    }
    if (isFlushing || isRunning === null) return;

    setIsFlushing(true);
    try {
      const response = await axios.post('/flush_test_results');
      if (response.data.status === 'success') {
        setTestResults([]);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error flushing results:', error);
      alert('Failed to flush results.');
    } finally {
      setIsFlushing(false);
    }
  };

  const handleRunRowClick = (runRowKey, run) => {
    if (deps.expandedRun === runRowKey) {
      setExpandedRun(null);
      return;
    }
    setExpandedRun(runRowKey);
    const normalizedRunId = String(run?.run_id || '').trim();
    if (normalizedRunId && !Array.isArray(run?.results)) {
      fetchRunDetails(normalizedRunId, { silent: true });
    }
  };

  const handleRunExport = async (event, run, absoluteIdx) => {
    event.stopPropagation();
    let targetRun = run;
    if (!Array.isArray(run?.results)) {
      const fetchedRun = await fetchRunDetails(run?.run_id, { silent: false });
      if (!fetchedRun || !Array.isArray(fetchedRun?.results) || fetchedRun.results.length === 0) {
        return;
      }
      targetRun = fetchedRun;
    }
    exportRunToCsv(targetRun, absoluteIdx);
  };

  return {
    runTestRig,
    terminateRun,
    terminateAllRuns,
    flushResults,
    fetchRunDetails,
    handleRunRowClick,
    handleRunExport,
    mergeUpdatedRunInState,
  };
};
